import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import { bearerHeaders, saveBlobInSession } from "shared/sessionDownload";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  emailOutcomeWording,
  type InquiryEmailDraft,
  type InquiryEmailResult,
  type InquiryPdf,
  type InquiryPdfResult,
} from "./inquiryDelivery.types";

type Severity = "success" | "info" | "warning" | "error";

/** The slice of a RAW Feathers service the panel calls. */
interface RawService {
  create: (data: object, params?: object) => Promise<unknown>;
}

const messageOf = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

/**
 * The question open in the Inquiry Queue's dialog, on its way to the client:
 * its PDF (#4211) and the email to the client's chosen contacts (#17).
 *
 * THE LIST of a question's PDFs is read through the `client-inquiry-pdfs`
 * ServiceStore (clients.stores.ts) and filtered here by question; no API entity
 * is kept in this store. MAKING a PDF goes through the RAW Feathers client,
 * whose refusals reject (a 403 or a 400 never reads as made), and then the PDF
 * is downloaded through the authenticated content stream, never after a sign-out.
 *
 * THE EMAIL's draft is read through the `client-inquiry-emails` ServiceStore,
 * keyed by the question's id, when the person opens the composer; this store
 * keeps only the FORM (who it goes to — nobody until the person ticks them —
 * the subject and text as edited, the PDF choice, "I have read it"). The send
 * goes through the RAW client; what its answer means is worded by
 * `emailOutcomeWording`, which never says "delivered".
 *
 * The dialog's store (InquiryQueueStore.select) tells this one which question is
 * open: opening it is a person's act, so the read follows the act.
 */
class InquiryDeliveryStore {
  rootStore: RootStore;
  inquiryId: number | null = null;

  pdfsLoading = false;
  pdfsError: string | null = null;

  making = false;
  makeError: string | null = null;
  /** Other clients the server found named in the client's text; the person removes them or confirms. */
  flaggedClients: string[] = [];
  othersConfirmed = false;
  /** The PDF just made, said once. */
  made: InquiryPdfResult | null = null;

  downloadError: string | null = null;

  // ---- the email (#17) ----
  /** The person opened the composer. Nothing is read for the email before. */
  composing = false;
  emailLoading = false;
  emailError: string | null = null;
  to: string[] = [];
  subject = "";
  text = "";
  attachPdf = false;
  reviewed = false;
  /** Other clients the draft or a refused send named; the person removes them or confirms. */
  emailFlagged: string[] = [];
  emailOthersConfirmed = false;
  /** Which draft the form was seeded from (the question's id); null before any. */
  formFor: number | null = null;
  sending = false;
  sendError: string | null = null;
  sendOutcome: { severity: Severity; text: string } | null = null;

  private seq = 0;
  private emailSeq = 0;
  /**
   * Bumped whenever the question the panel is for changes (dispose). A PDF or an
   * email still in flight carries the value it started under, and clears its
   * own `making` / `sending` flag only if that is unchanged (Codex r1: a slow
   * send for one question re-enabled the next question's Send button mid-send,
   * which a second click turned into a duplicate email to the client).
   */
  private actSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ---- services ------------------------------------------------------------

  private get pdfService(): ServiceStoreType | null {
    const rs = this.rootStore as unknown as {
      serviceStoreManager?: { getStore?: (name: string) => unknown };
      clientInquiryPdfsStore?: unknown;
    };
    return (
      ((rs.serviceStoreManager?.getStore?.("client-inquiry-pdfs") ?? rs.clientInquiryPdfsStore) as
        | ServiceStoreType
        | null
        | undefined) ?? null
    );
  }

  private get emailService(): ServiceStoreType | null {
    const rs = this.rootStore as unknown as {
      serviceStoreManager?: { getStore?: (name: string) => unknown };
      clientInquiryEmailsStore?: unknown;
    };
    return (
      ((rs.serviceStoreManager?.getStore?.("client-inquiry-emails") ?? rs.clientInquiryEmailsStore) as
        | ServiceStoreType
        | null
        | undefined) ?? null
    );
  }

  private raw(path: string): RawService | null {
    const app = (this.rootStore as unknown as { app?: { service: (p: string) => RawService } }).app;
    return app?.service(path) ?? null;
  }

  // ---- derived -------------------------------------------------------------

  /** This question's PDFs, newest first. */
  get pdfs(): InquiryPdf[] {
    if (this.inquiryId === null) return [];
    const rows = (this.pdfService?.entities ?? []) as unknown as InquiryPdf[];
    return rows
      .filter((p) => p.inquiryId === this.inquiryId)
      .sort((a, b) => String(b.generatedAt ?? "").localeCompare(String(a.generatedAt ?? "")));
  }

  /** The email draft for the open question, as the server composed it. */
  get emailDraft(): InquiryEmailDraft | null {
    if (this.inquiryId === null) return null;
    const rows = (this.emailService?.entities ?? []) as unknown as InquiryEmailDraft[];
    return rows.find((r) => r.id === this.inquiryId) ?? null;
  }

  /** Why Send is not available right now, in words; null when it is. */
  get emailBlocker(): string | null {
    const d = this.emailDraft;
    if (!d) return "The email has not been drafted yet.";
    if (d.blocked) return d.blocked;
    if (!d.email.configured) return "Email is not set up on this HQ yet: an admin needs to add the SendGrid key.";
    if (!this.to.length) return "Tick at least one of the client's contacts.";
    if (!this.subject.trim()) return "The email needs a subject.";
    if (!this.text.trim()) return "The email has no text.";
    if (!this.reviewed) return "Confirm you have read the email before it goes to the client.";
    return null;
  }

  get canSend(): boolean {
    return !this.sending && this.emailBlocker === null;
  }

  isRecipient(email: string): boolean {
    return this.to.includes(email);
  }

  // ---- the question --------------------------------------------------------

  /** The dialog opened this question (or closed, with null). */
  open(inquiryId: number | null) {
    if (inquiryId === this.inquiryId) return;
    this.dispose();
    this.inquiryId = inquiryId;
    if (inquiryId !== null) void this.loadPdfs();
  }

  async loadPdfs() {
    const ended = sessionMark(this.rootStore);
    const seq = ++this.seq;
    const id = this.inquiryId;
    const service = this.pdfService;
    if (id === null) return;
    if (!service) {
      this.pdfsError = "The client-inquiry-pdfs service is not available in this build.";
      return;
    }
    this.pdfsLoading = true;
    this.pdfsError = null;
    try {
      await service.find({ query: { inquiryId: id }, throwOnError: true } as never);
    } catch (err) {
      if (isSessionEnded(err) || ended() || seq !== this.seq) return;
      runInAction(() => {
        this.pdfsError = messageOf(err, "The question's PDFs could not be listed.");
      });
    } finally {
      if (!ended() && seq === this.seq) runInAction(() => (this.pdfsLoading = false));
    }
  }

  setOthersConfirmed(v: boolean) {
    this.othersConfirmed = v;
  }

  // ---- the PDF -------------------------------------------------------------

  /** Make the question's PDF, file it, and download it — one click. Sends nothing. */
  async makePdf(): Promise<boolean> {
    const ended = sessionMark(this.rootStore);
    const id = this.inquiryId;
    const raw = this.raw("client-inquiry-pdfs");
    if (id === null || !raw || this.making) return false;
    const token = this.actSeq;
    this.making = true;
    this.makeError = null;
    this.made = null;
    try {
      const res = (await raw.create({
        inquiryId: id,
        acknowledgedOtherClients: this.othersConfirmed ? [...this.flaggedClients] : [],
      })) as InquiryPdfResult | null;
      if (ended() || id !== this.inquiryId) return false;
      if (!res?.documentId) throw new Error("HQ did not say which PDF it made.");
      runInAction(() => {
        this.made = res;
      });
      await this.loadPdfs();
      await this.download(res);
      return true;
    } catch (err) {
      if (isSessionEnded(err) || ended() || id !== this.inquiryId) return false;
      const data = (err as { data?: { reason?: unknown; otherClients?: unknown } } | null)?.data;
      runInAction(() => {
        this.makeError = messageOf(err, "HQ could not make the PDF.");
        // The server names what it found; the person removes it or confirms it.
        if (data?.reason === "other-client" && Array.isArray(data.otherClients)) {
          const named = data.otherClients.filter((n): n is string => typeof n === "string");
          this.flaggedClients = [...new Set([...this.flaggedClients, ...named])];
          this.othersConfirmed = false;
        }
      });
      return false;
    } finally {
      if (!ended() && token === this.actSeq) runInAction(() => (this.making = false));
    }
  }

  /**
   * GET /documents/:id/content with the Bearer header and save the bytes — a
   * raw fetch the session guard cannot see, so the session is asked after every
   * await (shared/sessionDownload.ts): the previous person's PDF is never saved
   * in the next one's session.
   */
  async download(p: Pick<InquiryPdf, "documentId" | "fileName">): Promise<boolean> {
    const ended = sessionMark(this.rootStore);
    runInAction(() => {
      this.downloadError = null;
    });
    try {
      const { env } = await import("../../../config/environment");
      if (ended()) return false;
      const token = (this.rootStore as unknown as { authStore?: { token?: string | null } }).authStore?.token ?? null;
      const response = await fetch(`${env.apiUrl}/documents/${encodeURIComponent(p.documentId)}/content`, {
        headers: bearerHeaders(token),
      });
      if (ended()) return false;
      if (!response.ok) throw new Error(`The PDF could not be downloaded (${response.status}).`);
      const blob = await response.blob();
      return saveBlobInSession(ended, blob, p.fileName || "inquiry.pdf");
    } catch (err) {
      if (isSessionEnded(err) || ended()) return false;
      runInAction(() => {
        this.downloadError = messageOf(err, "The PDF could not be downloaded.");
      });
      return false;
    }
  }

  // ---- the email -----------------------------------------------------------

  /** The person chose to write the client an email: draft it. */
  async openEmail() {
    this.composing = true;
    this.sendOutcome = null;
    this.sendError = null;
    await this.loadEmail();
  }

  closeEmail() {
    this.composing = false;
    this.sendError = null;
  }

  /**
   * Read the draft. `keepForm` keeps what the person has written (a refused send
   * must not wipe their edits); otherwise the form is seeded afresh: nobody
   * ticked, and the PDF attached when there is no link into their app.
   */
  async loadEmail(options: { keepForm?: boolean } = {}) {
    const ended = sessionMark(this.rootStore);
    const seq = ++this.emailSeq;
    const id = this.inquiryId;
    const service = this.emailService;
    if (id === null) return;
    if (!service) {
      this.emailError = "The client-inquiry-emails service is not available in this build.";
      return;
    }
    this.emailLoading = true;
    this.emailError = null;
    try {
      await service.get(id, { throwOnError: true } as never);
      if (ended() || seq !== this.emailSeq || id !== this.inquiryId) return;
      runInAction(() => {
        const d = this.emailDraft;
        if (d && (!options.keepForm || this.formFor !== id)) {
          this.to = [];
          this.subject = d.subject;
          this.text = d.text;
          this.attachPdf = !d.link.url;
          this.reviewed = false;
          this.emailFlagged = [...d.otherClients];
          this.emailOthersConfirmed = false;
          this.formFor = id;
        }
      });
    } catch (err) {
      if (isSessionEnded(err) || ended() || seq !== this.emailSeq) return;
      runInAction(() => {
        this.emailError = messageOf(err, "The email could not be drafted.");
      });
    } finally {
      if (!ended() && seq === this.emailSeq) runInAction(() => (this.emailLoading = false));
    }
  }

  toggleRecipient(email: string) {
    this.to = this.isRecipient(email) ? this.to.filter((e) => e !== email) : [...this.to, email];
  }

  setSubject(v: string) {
    this.subject = v;
    this.reviewed = false;
  }

  setText(v: string) {
    // A changed text is a text nobody has read yet, and a confirmation about the old one says nothing about the new.
    this.text = v;
    this.reviewed = false;
    this.emailOthersConfirmed = false;
  }

  setAttachPdf(v: boolean) {
    this.attachPdf = v;
    this.reviewed = false;
  }

  setReviewed(v: boolean) {
    this.reviewed = v;
  }

  setEmailOthersConfirmed(v: boolean) {
    this.emailOthersConfirmed = v;
  }

  async sendEmail(): Promise<boolean> {
    const ended = sessionMark(this.rootStore);
    const id = this.inquiryId;
    const raw = this.raw("client-inquiry-emails");
    const stage = this.emailDraft?.stage ?? "";
    if (id === null || !raw || !this.canSend) return false;
    const token = this.actSeq;
    this.sending = true;
    this.sendError = null;
    this.sendOutcome = null;
    let accepted = false;
    try {
      const result = (await raw.create({
        inquiryId: id,
        to: [...this.to],
        subject: this.subject,
        text: this.text,
        attachPdf: this.attachPdf,
        reviewed: this.reviewed,
        acknowledgedOtherClients: this.emailOthersConfirmed ? [...this.emailFlagged] : [],
      })) as InquiryEmailResult;
      if (ended() || id !== this.inquiryId) return false;
      accepted = Boolean(result?.accepted);
      runInAction(() => {
        this.sendOutcome = emailOutcomeWording(result, stage);
      });
    } catch (err) {
      if (isSessionEnded(err) || ended() || id !== this.inquiryId) return false;
      const data = (err as { data?: { reason?: unknown; otherClients?: unknown } } | null)?.data;
      runInAction(() => {
        this.sendError = messageOf(err, "HQ could not send this email.");
        if (data?.reason === "other-client" && Array.isArray(data.otherClients)) {
          const named = data.otherClients.filter((n): n is string => typeof n === "string");
          this.emailFlagged = [...new Set([...this.emailFlagged, ...named])];
          this.emailOthersConfirmed = false;
        }
      });
    } finally {
      if (!ended() && token === this.actSeq) runInAction(() => (this.sending = false));
    }
    if (ended()) return accepted;
    // The draft carries the "sent about this question" list: re-read it. A send
    // SendGrid took starts the next one fresh; anything else keeps the edits.
    await this.loadEmail({ keepForm: !accepted });
    if (accepted) await this.loadPdfs();
    return accepted;
  }

  /** Sign-out (RootStore.purgeUserData): every field back to its constructed value. */
  reset() {
    this.dispose();
  }

  dispose() {
    // A read, a PDF or an email still in flight belongs to the question being left.
    this.seq += 1;
    this.actSeq += 1;
    this.inquiryId = null;
    this.pdfsLoading = false;
    this.pdfsError = null;
    this.making = false;
    this.makeError = null;
    this.flaggedClients = [];
    this.othersConfirmed = false;
    this.made = null;
    this.downloadError = null;
    this.emailSeq += 1;
    this.composing = false;
    this.emailLoading = false;
    this.emailError = null;
    this.to = [];
    this.subject = "";
    this.text = "";
    this.attachPdf = false;
    this.reviewed = false;
    this.emailFlagged = [];
    this.emailOthersConfirmed = false;
    this.formFor = null;
    this.sending = false;
    this.sendError = null;
    this.sendOutcome = null;
  }
}

export default InquiryDeliveryStore;
